import React, {
  useEffect,
  useContext,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { PropTypes } from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import format from 'string-template';
import moment from 'moment';

import { ContractStatus, getContractObj } from '../../../../../CoachTools/utils/userContract';
import { generateTerms } from '../../../../../CoachTools/utils/userContractTerms';
import UserContractContext from '../../../../../context/UserContractContext';
import CoachAssignmentContext from '../../../../../context/CoachAssignmentContext';
import OnboardingFlowContext from '../../../../../context/OnboardingFlowContext';
import PlanContext from '../../../../../context/PlanContext';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useAuthentication from '../../../../../hooks/useAuthentication';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useStorage from '../../../../../hooks/useStorage';
import { formatAmoutInCents, formatCurrencyCents } from '../../../../../utils/formatters';
import { getDeviceType } from '../../../../../CoachTools/components/ClientQuickInfoModal/utils';
import subscriptionSubmissionTypes from '../../subscriptionSubmissionTypes';
import AppCustomization from '../../../../../CoachTools/Model/AppCustomization';
import Product from '../../../../../Model/Product';
import { ColorModes } from '../../../../../Model/Coach';

import ConfirmDialog from '../../../../../CoachTools/components/ConfirmDialog';
import colors from '../../../../../styles/colors';

import {
  SubscriptionCreationState,
} from '../../SubscriptionMetadata';

import SuccessfulPaymentNotificationCard from '../SuccessfulPaymentNotificationCard';
import FailureNotificationCard from '../FailureNotificationCard';
import PaymentForm from '../PaymentForm';

import ShareASale from '../ShareASale';
import CheckoutActions from '../CheckoutActions';
import AccountCreation from '../AccountCreation';

import SubmitButton from '../SubmitButton';

import {
  Container,
  StyledOnboardingCard,
  FreeTitle,
  InfoContainer,
  ClockIcon,
  WarningContainer,
  BellCrossedIcon,
  Step,
  StepContainer,
  AgreementContainer,
  StyledContractDetails,
  NoPaymentContainer,
  HeaderContainer,
  CoachHero,
  LeftSideWrapper,
  RightSideWrapper,
  StepTitle,
  StepSubTitle,
  StyledAppIcon,
  BillingDetails,
  MonthlyBillingContainer,
  RecurringPaymentContainer,
  MonthlyPriceContainer,
  MonthText,
  ProductName,
  PackageInfos,
  PackageInfo,
  ImportantNotes,
  ImportantNotesTitle,
  StyledListItem,
  UserDetailsSection,
  LogoutButton,
  UserName,
  StyledList,
  StyledWarningIcon,
  StyledDoneIcon,
  DesktopView,
  MobileView,
} from './styles';

import texts from './texts.json';

const RecurringOption = {
  YEARLY: 12,
  MONTHLY: 1,
};

const SubscriptionCard = ({ subscriptionFormData }) => {
  const {
    subscriptionControllerState,
    subscriptionState,
    applePayPaymentRequest,
    handleSubscriptionEndResult,
    stripeErrorState,
    handleSubscriptionSubmissionWithCreditCard,
    submitAuthenticationConfirmation,
    handleNoPaymentSubscriptionSubmission,
    onAccountFormSubmitted,
    accountCreationState,
    isOnboardingCompleted,
    userDetail: {
      firstName,
      lastName,
      email,
      phoneNumber,
    },
  } = subscriptionFormData;

  const [showPaymentConfirmationModal, setShowPaymentConfirmationModal] = useState(false);
  const [authConfirmationDetails, setAuthConfirmationDetails] = useState(null);
  const [subscriptionFlowError, setErrorState] = useState({});
  const [coachAppIconUrl, setCoachAppIconUrl] = useState('');
  const [coachAppCustomization, setCoachAppCustomization] = useState({});
  const [product, setProduct] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(subscriptionSubmissionTypes.CARD_PAYMENT);

  // Contract-related state
  const { contractDoc, newTermsAvailable } = useContext(UserContractContext);
  const [contract, setContract] = useState(null);

  const confirmPaymentForSubscription = useCallback((isConfirmed) => {
    submitAuthenticationConfirmation({ isConfirmed });
  }, [submitAuthenticationConfirmation]);

  useEffect(() => {
    if (subscriptionState === SubscriptionCreationState.WAITING_FOR_INITIAL_PAYMENT_AUTHORIZATION) {
      setShowPaymentConfirmationModal(true);
    }
  }, [subscriptionState]);

  useEffect(() => {
    let errorTmp = {
      isError: false,
      responseErrorMsg: null,
    };
    if (subscriptionState === SubscriptionCreationState.WAITING_FOR_VALID_PAYMENT_METHOD) {
      errorTmp = {
        isError: true,
        responseErrorMsg: 'Subscription payment validation failed. Please use a different card.',
      };
    }
    if (stripeErrorState && stripeErrorState.message) {
      errorTmp = {
        isError: true,
        responseErrorMsg: stripeErrorState.message,
      };
    }
    setErrorState(errorTmp);
  }, [subscriptionState, stripeErrorState]);

  useEffect(() => {
    if (authConfirmationDetails) {
      setShowPaymentConfirmationModal(false);
      confirmPaymentForSubscription(authConfirmationDetails.isConfirmed);
      setAuthConfirmationDetails(null);
    }
  }, [authConfirmationDetails, confirmPaymentForSubscription]);

  const onAuthConfirmationResponse = useCallback((isConfirmed) => {
    setShowPaymentConfirmationModal(false);
    setAuthConfirmationDetails({ isConfirmed });
  }, [setShowPaymentConfirmationModal, setAuthConfirmationDetails]);

  const { authUser } = useSessionStore();
  const { signOut } = useAuthentication();
  const isComponentMountedRef = useComponentMounted();
  const { getBlobUrl } = useStorage();

  const [isProceed, setIsProceed] = useState(!!authUser);

  const {
    currentCoach,
  } = useContext(CoachAssignmentContext);

  useEffect(() => {
    const fetchCoachConfig = async () => {
      const appCustomization = await AppCustomization.getAppCustomizationByCoach(currentCoach.id);
      const appIconUrl = await getBlobUrl(appCustomization.webAppIconRef);
      if (isComponentMountedRef.current) {
        setCoachAppCustomization(appCustomization);
        setCoachAppIconUrl(appIconUrl);
      }
    };
    fetchCoachConfig();
  }, [currentCoach, isComponentMountedRef, getBlobUrl]);

  const isDarkTheme = currentCoach.checkoutPageColorMode === ColorModes.DARK;
  const isDesktopView = getDeviceType() === 'DESKTOP';

  const [isAcceptedTerms, setIsAcceptedTerms] = useState(false);

  const dialogStyles = {
    '& .MuiPaper-root': {
      background: isDarkTheme ? colors.base.beta : colors.base.alpha,
    },
    '& .MuiDialogTitle-root': {
      color: isDarkTheme ? colors.base.alpha : colors.base.beta,
    },
    '& .MuiDialogContentText-root': {
      color: colors.shades.gamma10,
    },
  };

  const {
    startAtDate,
  } = useContext(OnboardingFlowContext);

  const {
    plan: {
      isFree: isFreePlan,
      totalPriceInCents,
      initialPaymentInCents,
      recurringBillingMonths,
      cancelAt,
      currency,
      cancelAtPeriodEnd,
      id: priceId,
      minSubscriptionMonths,
      initialTerm = recurringBillingMonths,
    },
  } = useContext(PlanContext);

  // Create the contract object so that we can display the whole list of terms for every subscription.
  useEffect(() => {
    let contractObj;
    if (contractDoc) {
      contractObj = getContractObj(contractDoc);
    } else {
      // TODO: Remove this once we remove support for lagacy subscriptions (PIF and CHALLENGES using onboarding links).
      contractObj = {
        startDate: startAtDate || moment(),
        initialPayment: formatAmoutInCents(totalPriceInCents, { minimumFractionDigits: 2 }),
        monthlyPayment: formatAmoutInCents(totalPriceInCents, { minimumFractionDigits: 2 }),
        initialTerm: recurringBillingMonths || initialTerm,
        minSubscriptionMonths: minSubscriptionMonths || 3,
        currency,
        priceId,
        cancelAtPeriodEnd,
        coachConditions: currentCoach.termsAndConditionsText,
        /*
          If there is no contract, then it is a legacy plan, so we force the version to 1. Version 1 contracts allow
          for PIF contracts, for example, and challenges. In those cases, we create a contract in the server once the
          subscription is created.
        */
        version: 1,
      };
    }

    // If the start date is in the past, we set it to today in the checkout page, and we remove the upfront payment.
    if (contractObj.startDate.isBefore(moment())) {
      contractObj.startDate = moment();
      contractObj.upfrontPayment = 0;
    }

    setContract(contractObj);
  }, [
    contractDoc,
    currency,
    priceId,
    startAtDate,
    totalPriceInCents,
    cancelAtPeriodEnd,
    minSubscriptionMonths,
    currentCoach.termsAndConditionsText,
    initialTerm,
    recurringBillingMonths,
  ]);

  useEffect(() => {
    const fetchProduct = async () => {
      const productDoc = new Product(contractDoc.productId);
      await productDoc.init();
      setProduct(productDoc);
    };
    if (contractDoc) {
      fetchProduct();
    }
  }, [contractDoc]);

  // Prepare form components based on plan pricing (free or not)
  const onSubmit = isFreePlan ? handleNoPaymentSubscriptionSubmission : handleSubscriptionSubmissionWithCreditCard;

  /*
    Subscription form is loading when subscription creation is not complete and the controller is busy, or when the
    subscription creation is complete but the user onboarding is not complete yet
  */
  const subscriptionIsLoading = useMemo(() => (
    (subscriptionState !== SubscriptionCreationState.SUCCESSFUL_COMPLETION && subscriptionControllerState.isBusy)
    || (subscriptionState === SubscriptionCreationState.SUCCESSFUL_COMPLETION && !isOnboardingCompleted)
  ), [
    subscriptionState,
    subscriptionControllerState,
    isOnboardingCompleted,
  ]);

  const renderAccountCreationStep = useCallback(() => {
    if (authUser) {
      return null;
    }

    return (
      <StepContainer>
        <Step
          $background={coachAppCustomization?.primaryColor}
          $isDarkTheme={isDarkTheme}
        >
          {format(texts.step, { number: '1', total: isFreePlan ? 2 : 3 })}
        </Step>
        <StepTitle $isDarkTheme={isDarkTheme}>
          {texts.createAccount}
        </StepTitle>
        <StepSubTitle>{texts.signUpContent}</StepSubTitle>
        <AccountCreation
          email={email}
          firstName={firstName}
          lastName={lastName}
          phoneNumber={phoneNumber}
          onSubmit={onAccountFormSubmitted}
          state={accountCreationState}
          color={coachAppCustomization?.primaryColor}
          isDarkTheme={isDarkTheme}
        />
      </StepContainer>
    );
  }, [
    authUser,
    email,
    firstName,
    lastName,
    phoneNumber,
    onAccountFormSubmitted,
    accountCreationState,
    coachAppCustomization,
    isDarkTheme,
    isFreePlan,
  ]);

  const renderCheckoutStep = useCallback(() => {
    if (authUser) {
      if (isAcceptedTerms) {
        return (
          <StepContainer>
            <Step
              $background={coachAppCustomization?.primaryColor}
              $isDarkTheme={isDarkTheme}
            >
              {format(texts.step, { number: '3', total: isFreePlan ? 2 : 3 })}
            </Step>
            <StepTitle $isDarkTheme={isDarkTheme}>
              {texts.checkout}
            </StepTitle>
            <StepSubTitle />
            {!!applePayPaymentRequest && (
              <CheckoutActions
                selectedPaymentType={selectedPaymentMethod}
                setSelectedPaymentType={setSelectedPaymentMethod}
                isDarkTheme={isDarkTheme}
                applePayPaymentRequest={applePayPaymentRequest}
              />
            )}
            {isAcceptedTerms && (
              <PaymentForm
                isLoading={subscriptionIsLoading}
                errorMessage={subscriptionFlowError?.responseErrorMsg}
                onSubmit={onSubmit}
                isDarkTheme={isDarkTheme}
                color={coachAppCustomization?.primaryColor}
                contract={contract}
                isApplePayView={selectedPaymentMethod === subscriptionSubmissionTypes.APPLE_PAY}
              />
            )}
          </StepContainer>
        );
      }

      // Generate the contract terms dynamically, since we don't always have a contract document at this point.
      const contractTerms = generateTerms(contract);
      return (
        <StepContainer>
          <Step
            $background={coachAppCustomization?.primaryColor}
            $isDarkTheme={isDarkTheme}
          >
            {format(texts.step, { number: '2', total: isFreePlan ? 2 : 3 })}
          </Step>
          <StepTitle $isDarkTheme={isDarkTheme}>
            {texts.termsOfService}
          </StepTitle>
          <StepSubTitle>{texts.termsContent}</StepSubTitle>
          <AgreementContainer>
            {!!contract && (
              <StyledContractDetails
                contractTerms={contractTerms}
                coachConditions={contract.coachConditions}
                isDarkTheme={isDarkTheme}
                isCheckoutPageview
              />
            )}
          </AgreementContainer>
          {
            isFreePlan
              ? (
                <NoPaymentContainer>
                  <SubmitButton
                    onClick={onSubmit}
                    isLoading={subscriptionIsLoading}
                    $background={coachAppCustomization?.primaryColor}
                    $isDarkTheme={isDarkTheme}
                  >
                    {texts.acceptAndContinue}
                  </SubmitButton>
                </NoPaymentContainer>
              ) : (
                <SubmitButton
                  $background={coachAppCustomization?.primaryColor}
                  $isDarkTheme={isDarkTheme}
                  onClick={() => setIsAcceptedTerms(true)}
                >
                  {texts.acceptAndContinue}
                </SubmitButton>
              )
          }
        </StepContainer>
      );
    }
    return null;
  }, [
    authUser,
    subscriptionFlowError,
    onSubmit,
    isAcceptedTerms,
    subscriptionIsLoading,
    isFreePlan,
    contract,
    coachAppCustomization,
    isDarkTheme,
    selectedPaymentMethod,
    applePayPaymentRequest,
  ]);

  const renderPackageDetails = useCallback(() => (
    <>
      <CoachHero src={currentCoach.avatar} />
      <BillingDetails>
        <MonthlyBillingContainer $isDarkTheme={isDarkTheme}>
          <ProductName>{product?.name || texts.oneOnOneCoaching}</ProductName>
          <MonthlyPriceContainer $addGap={initialTerm > 1}>
            <RecurringPaymentContainer $isDarkTheme={isDarkTheme}>
              {formatCurrencyCents(initialPaymentInCents || totalPriceInCents, currency)}
            </RecurringPaymentContainer>
            {initialTerm > 1 ? (
              <MonthText>
                {format(texts.forMonths, { months: initialTerm })}
              </MonthText>
            ) : (
              <MonthText>
                {recurringBillingMonths === RecurringOption.MONTHLY ? texts.month : texts.year}
              </MonthText>
            )}
          </MonthlyPriceContainer>
        </MonthlyBillingContainer>
        <PackageInfos $isDarkTheme={isDarkTheme}>
          {(product?.isCheckInFeedBackEnabled || product?.isChatEnabled) && (
            <PackageInfo>
              <StyledDoneIcon />
              {format(texts.oneToOneCoaching, { coachName: currentCoach.name })}
            </PackageInfo>
          )}
          <PackageInfo>
            <StyledDoneIcon />
            {texts.workout}
          </PackageInfo>
          {product?.isMealPlanEnabled && (
            <PackageInfo>
              <StyledDoneIcon />
              {texts.mealPlan}
            </PackageInfo>
          )}
          {product?.isCheckInFeedBackEnabled && (
            <PackageInfo>
              <StyledDoneIcon />
              {texts.checkInFeedback}
            </PackageInfo>
          )}
          <PackageInfo>
            <StyledDoneIcon />
            {texts.accountabilityAndHabits}
          </PackageInfo>
          {product?.isVideoFeedBackEnabled && (
            <PackageInfo>
              <StyledDoneIcon />
              {texts.videoFeedback}
            </PackageInfo>
          )}
          {product?.isChatEnabled && (
            <PackageInfo>
              <StyledDoneIcon />
              {texts.chat}
            </PackageInfo>
          )}
        </PackageInfos>
        <ImportantNotes>
          <ImportantNotesTitle>
            <StyledWarningIcon />
            {texts.importantInformation}
          </ImportantNotesTitle>
          <StyledList>
            {initialTerm === 1 && (
              <StyledListItem>
                {recurringBillingMonths === RecurringOption.MONTHLY ? texts.monthlyBilling : texts.annualBilling}
              </StyledListItem>
            )}
            <StyledListItem>
              {format(texts.minimumCommitment, {
                months: minSubscriptionMonths,
              })}
            </StyledListItem>
            <StyledListItem>{texts.autoRenewal}</StyledListItem>
          </StyledList>
        </ImportantNotes>
      </BillingDetails>
      {isFreePlan && (
        <FreeTitle $isDarkTheme={isDarkTheme}>{texts.freePlanDescription}</FreeTitle>
      )}
      {
        !!startAtDate && (
          <InfoContainer $isDarkTheme={isDarkTheme}>
            <ClockIcon />
            <span>{format(texts.billingCycleStart, startAtDate.format('LL'))}</span>
          </InfoContainer>
        )
      }
      {
        !!cancelAt && (
          <WarningContainer $isDarkTheme={isDarkTheme}>
            <BellCrossedIcon />
            <span>{format(texts.subscriptionEnds, cancelAt.format('LL'))}</span>
          </WarningContainer>
        )
      }
    </>
  ), [
    currentCoach,
    product,
    initialPaymentInCents,
    recurringBillingMonths,
    minSubscriptionMonths,
    isFreePlan,
    startAtDate,
    cancelAt,
    isDarkTheme,
    currency,
    initialTerm,
    totalPriceInCents,
  ]);

  if (subscriptionState === SubscriptionCreationState.FAILURE_COMPLETION
    || subscriptionState === SubscriptionCreationState.FAILURE_NETWORK) {
    return (
      <Container $isDarkTheme={isDarkTheme}>
        <FailureNotificationCard
          message={texts.notification.defaultFailure}
          onConfirmingFinalResult={handleSubscriptionEndResult}
          isDarkTheme={isDarkTheme}
          color={coachAppCustomization?.primaryColor}
        />
      </Container>
    );
  }

  if (subscriptionState === SubscriptionCreationState.ACTIVE_SUBSCRIPTION_EXISTS) {
    return (
      <Container $isDarkTheme={isDarkTheme}>
        <SuccessfulPaymentNotificationCard
          text={texts.notification.subscriptionExists}
          onConfirmingFinalResult={handleSubscriptionEndResult}
          isDarkTheme={isDarkTheme}
          color={coachAppCustomization?.primaryColor}
        />
      </Container>
    );
  }

  if ((contractDoc && contractDoc.exists)
    && (contractDoc.status === ContractStatus.FAILED)) {
    return (
      <Container $isDarkTheme={isDarkTheme}>
        <FailureNotificationCard
          message={texts.notification.defaultFailure}
          onConfirmingFinalResult={handleSubscriptionEndResult}
          isDarkTheme={isDarkTheme}
          color={coachAppCustomization?.primaryColor}
        />
      </Container>
    );
  }

  if (isOnboardingCompleted) {
    return (
      <Container $isDarkTheme={isDarkTheme}>
        <ShareASale
          userId={authUser.uid}
        />
        <SuccessfulPaymentNotificationCard
          texts={texts.notification.success}
          onConfirmingFinalResult={handleSubscriptionEndResult}
          isDarkTheme={isDarkTheme}
          color={coachAppCustomization?.primaryColor}
        />
      </Container>
    );
  }

  return (
    <Container $isDarkTheme={isDarkTheme}>
      <HeaderContainer>
        {!!coachAppIconUrl && (
          <StyledAppIcon
            src={coachAppIconUrl}
            $isDisplay={!isProceed}
          />
        )}
        {!!authUser && !!coachAppIconUrl && (
          <UserDetailsSection>
            <UserName>
              {`${firstName} ${lastName}`}
            </UserName>
            <LogoutButton
              onClick={signOut}
              $isDarkTheme={isDarkTheme}
            >
              {texts.logOut}
            </LogoutButton>
          </UserDetailsSection>
        )}
      </HeaderContainer>
      <StyledOnboardingCard $isDarkTheme={isDarkTheme}>
        {isDesktopView ? (
          <DesktopView>
            <LeftSideWrapper $isDarkTheme={isDarkTheme}>
              {renderPackageDetails()}
            </LeftSideWrapper>
            <RightSideWrapper $isDarkTheme={isDarkTheme}>
              {renderAccountCreationStep()}
              {renderCheckoutStep()}
            </RightSideWrapper>
          </DesktopView>
        ) : (
          <MobileView>
            {(!isProceed && !authUser) ? (
              <LeftSideWrapper $isDarkTheme={isDarkTheme}>
                {renderPackageDetails()}
                <SubmitButton
                  $background={coachAppCustomization?.primaryColor}
                  $isDarkTheme={isDarkTheme}
                  onClick={() => setIsProceed(true)}
                >
                  {texts.proceed}
                </SubmitButton>
              </LeftSideWrapper>
            ) : (
              <RightSideWrapper $isDarkTheme={isDarkTheme}>
                {renderAccountCreationStep()}
                {renderCheckoutStep()}
              </RightSideWrapper>
            )}
          </MobileView>
        )}
      </StyledOnboardingCard>
      <ConfirmDialog
        isOpen={showPaymentConfirmationModal}
        onConfirm={() => onAuthConfirmationResponse(true)}
        onCancel={() => onAuthConfirmationResponse(false)}
        dialogTexts={{
          title: texts.confirmDialog.title,
          content: texts.confirmDialog.content,
        }}
        sx={dialogStyles}
      />
      <ConfirmDialog
        isOpen={newTermsAvailable}
        onConfirm={() => window.location.reload()}
        dialogTexts={{
          title: texts.refreshDialog.title,
          content: texts.refreshDialog.content,
          confirm: texts.refreshDialog.confirm,
        }}
        sx={dialogStyles}
      />
    </Container>
  );
};

SubscriptionCard.propTypes = {
  subscriptionFormData: PropTypes.object.isRequired,
};

export default compose(
  observer,
)(SubscriptionCard);
